import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState, money } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeftRight, Plus, Trash2, ArrowDownLeft, ArrowUpRight, Recycle } from "lucide-react";

const CATS = [
  { v: "raw_wood", l: "Raw Wood" }, { v: "hardware", l: "Hardware" },
  { v: "polish", l: "Polish" }, { v: "finished_goods", l: "Finished Goods" }, { v: "scrap", l: "Scrap" },
];
const CAT_L = Object.fromEntries(CATS.map((c) => [c.v, c.l]));
const empty = { entry_type: "in", category: "raw_wood", item: "", quantity: 0, unit: "nos", party: "", reference: "", work_value: 0, remarks: "", entry_date: "" };

export default function Registers() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [tab, setTab] = useState("all");

  const load = () => api.get("/registers").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/registers", { ...form, quantity: Number(form.quantity), work_value: Number(form.work_value) });
      toast.success("Entry recorded");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/registers/${id}`); toast.success("Deleted"); load(); };

  const shown = tab === "all" ? items : tab === "scrap" ? items.filter((i) => i.category === "scrap") : items.filter((i) => i.entry_type === tab);
  const inQty = items.filter((i) => i.entry_type === "in").reduce((s, i) => s + i.quantity, 0);
  const outQty = items.filter((i) => i.entry_type === "out").reduce((s, i) => s + i.quantity, 0);
  const scrapVal = items.filter((i) => i.category === "scrap").reduce((s, i) => s + i.work_value, 0);

  return (
    <div className="space-y-8">
      <PageHeader title="In / Out & Scrap" subtitle="Stock movement registers, work value calculations and scrap management." icon={ArrowLeftRight}
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-register-button" className="gap-2 transition-transform duration-200 hover:-translate-y-px"><Plus className="h-4 w-4" /> New Entry</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-heading">Register Entry</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Type</Label>
                    <Select value={form.entry_type} onValueChange={(v) => setForm({ ...form, entry_type: v })}>
                      <SelectTrigger data-testid="reg-type"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="in">Inward</SelectItem><SelectItem value="out">Outward</SelectItem></SelectContent>
                    </Select></div>
                  <div className="space-y-2"><Label>Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="reg-category"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="col-span-2 space-y-2"><Label>Item</Label>
                    <Input data-testid="reg-item" value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })} placeholder="Sheesham logs" /></div>
                  <div className="space-y-2"><Label>Quantity</Label>
                    <Input type="number" data-testid="reg-qty" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Unit</Label>
                    <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="cft / kg / nos" /></div>
                  <div className="space-y-2"><Label>Party / Section</Label>
                    <Input value={form.party} onChange={(e) => setForm({ ...form, party: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Work / Value ₹</Label>
                    <Input type="number" data-testid="reg-value" value={form.work_value} onChange={(e) => setForm({ ...form, work_value: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Date</Label>
                    <Input type="date" value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Reference</Label>
                    <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></div>
                </div>
              </div>
              <DialogFooter><Button data-testid="reg-save" onClick={create} disabled={!form.item}>Save Entry</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        } />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card className="flex items-center gap-3 rounded-md border-border p-4 shadow-none">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-sage/15 text-sage"><ArrowDownLeft className="h-5 w-5" /></div>
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Total Inward</p><p className="font-heading text-xl font-bold">{inQty}</p></div>
        </Card>
        <Card className="flex items-center gap-3 rounded-md border-border p-4 shadow-none">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#4A5D6E]/15 text-[#4A5D6E]"><ArrowUpRight className="h-5 w-5" /></div>
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Total Outward</p><p className="font-heading text-xl font-bold">{outQty}</p></div>
        </Card>
        <Card className="flex items-center gap-3 rounded-md border-border p-4 shadow-none">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-destructive/10 text-destructive"><Recycle className="h-5 w-5" /></div>
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Scrap Value</p><p className="font-heading text-xl font-bold">{money(scrapVal)}</p></div>
        </Card>
      </div>

      <div className="flex flex-wrap gap-2">
        {[["all", "All"], ["in", "Inward"], ["out", "Outward"], ["scrap", "Scrap"]].map(([v, l]) => (
          <button key={v} data-testid={`reg-tab-${v}`} onClick={() => setTab(v)}
            className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors duration-200 ${tab === v ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:border-primary/40"}`}>{l}</button>
        ))}
      </div>

      {shown.length === 0 ? (
        <EmptyState icon={ArrowLeftRight} title="No entries" hint="Record inward/outward movement or scrap here." />
      ) : (
        <Card className="overflow-hidden rounded-md border-border shadow-none">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Type</TableHead><TableHead>Category</TableHead><TableHead>Item</TableHead>
                <TableHead className="text-right">Qty</TableHead><TableHead>Party</TableHead>
                <TableHead className="text-right">Value</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((r) => (
                <TableRow key={r.id} data-testid={`reg-row-${r.id}`} className="transition-colors duration-200 hover:bg-muted/40">
                  <TableCell>
                    <Badge variant="outline" className={`rounded-full text-[11px] font-semibold ${r.entry_type === "in" ? "border-sage/30 bg-sage/10 text-sage" : "border-[#4A5D6E]/30 bg-[#4A5D6E]/10 text-[#4A5D6E]"}`}>
                      {r.entry_type === "in" ? "IN" : "OUT"}
                    </Badge>
                  </TableCell>
                  <TableCell>{CAT_L[r.category]}</TableCell>
                  <TableCell className="font-medium">{r.item}</TableCell>
                  <TableCell className="text-right">{r.quantity} {r.unit}</TableCell>
                  <TableCell className="text-muted-foreground">{r.party || "—"}</TableCell>
                  <TableCell className="text-right font-heading font-semibold">{r.work_value ? money(r.work_value) : "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" data-testid={`reg-delete-${r.id}`} onClick={() => remove(r.id)}
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
